import React, { useMemo } from "react";
import {
  Box,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Chip,
  Tooltip,
} from "@mui/material";
import GlassCard from "../Cards/GlassCard";
import CommonDataGrid from "./CommonDataGrid";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import LocalFireDepartmentRoundedIcon from "@mui/icons-material/LocalFireDepartmentRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  gradientTextSx,
  PRIMARY,
  SUCCESS,
  WARNING,
  DANGER,
  GOLD,
  GOLD_TEXT,
  SILVER,
  SILVER_TEXT,
  BRONZE,
  GOLD_ROW_DARK,
  GOLD_ROW_LIGHT,
  PRIMARY_ROW_HOVER_DARK,
  PRIMARY_ROW_HOVER_LIGHT,
  WARNING_ROW_DARK,
  WARNING_ROW_LIGHT,
  WARNING_ROW_HOVER_DARK,
  WARNING_ROW_HOVER_LIGHT,
} from "../../utils/colors";

// ── Rank badge ────────────────────────────────────────────────────────────────
const RANK_STYLE = {
  1: { bg: GOLD,   color: GOLD_TEXT,   label: "🥇" },
  2: { bg: SILVER, color: SILVER_TEXT, label: "🥈" },
  3: { bg: BRONZE, color: "#fff",      label: "🥉" },
};

const RankBadge = ({ rank }) => {
  const style = RANK_STYLE[rank];
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 32,
        height: 22,
        borderRadius: "11px",
        px: 1,
        bgcolor: style ? style.bg : "transparent",
        color: style ? style.color : "text.secondary",
        fontWeight: 800,
        fontSize: "0.75rem",
        lineHeight: 1,
      }}
    >
      {style ? style.label : `#${rank}`}
    </Box>
  );
};

// ── Helper: format date ───────────────────────────────────────────────────────
const fmtDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString(undefined, {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch {
    return "—";
  }
};

// ── Custom row-class overrides (gold & inactive) ───────────────────────
const baseGridSx = {
  // gold top-3 rows (overallLeaderboard)
  "& .row-gold": {
    bgcolor: (theme) =>
      theme.palette.mode === "dark" ? GOLD_ROW_DARK : GOLD_ROW_LIGHT,
  },
  "& .row-gold:hover": {
    bgcolor: (theme) =>
      theme.palette.mode === "dark" ? PRIMARY_ROW_HOVER_DARK : PRIMARY_ROW_HOVER_LIGHT,
  },
  // warning rows (inactiveStudents)
  "& .row-inactive": {
    bgcolor: (theme) =>
      theme.palette.mode === "dark" ? WARNING_ROW_DARK : WARNING_ROW_LIGHT,
  },
  "& .row-inactive:hover": {
    bgcolor: (theme) =>
      theme.palette.mode === "dark" ? WARNING_ROW_HOVER_DARK : WARNING_ROW_HOVER_LIGHT,
  },
};

// ── Shared DataGrid props ─────────────────────────────────────────────────────
const BASE_GRID_PROPS = {
  disableColumnMenu: true,
  autoHeight: true,
};

// ── Section card wrapper ──────────────────────────────────────────────────────
const SectionCard = ({ icon: Icon, iconColor, title, children }) => (
  <GlassCard
    sx={{
      borderRadius: 3,
      height: "100%",
      display: "flex",
      flexDirection: "column",
    }}
  >
    <CardContent sx={{ p: 2, pb: "12px !important", flexGrow: 1, display: "flex", flexDirection: "column" }}>
      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
        <Icon sx={{ color: iconColor, fontSize: 18 }} />
        <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: "0.9rem" }}>
          {title}
        </Typography>
      </Box>
      {children}
    </CardContent>
  </GlassCard>
);

// ── "All active" no-rows overlay for inactive section ─────────────────────────
const AllActiveOverlay = () => (
  <Box display="flex" justifyContent="center" alignItems="center" height="100%" py={3}>
    <Typography variant="body2" sx={{ color: SUCCESS, fontWeight: 600 }}>
      🎉 All students are active!
    </Typography>
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────────
const LeetCodeLeaderboard = ({ leaderboard, loading }) => {
  const {
    overallLeaderboard   = [],
    hardProblemCrushers  = [],
    mostImprovedStudents = [],
    weeklyCodingRank     = [],
    inactiveStudents     = [],
  } = leaderboard ?? {};

  // ── Pre-process rows that need computed fields ──────────────────────────────
  const improvedRows = useMemo(() =>
    mostImprovedStudents.map((s) => ({
      ...s,
      id: s.rank,
      prevTotal: Math.max(0, (s.syncedSolvedProblems ?? 0) - (s.improvement ?? 0)),
    })),
  [mostImprovedStudents]);

  const inactiveRows = useMemo(() =>
    inactiveStudents.map((s, i) => ({ ...s, id: `${s.rollNumber ?? "u"}-${i}` })),
  [inactiveStudents]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={6}>
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary" ml={2}>
          Loading leaderboard…
        </Typography>
      </Box>
    );
  }

  if (!leaderboard) return null;

  // ── Column definitions ──────────────────────────────────────────────────────

  const overallCols = [
    {
      field: "rank", headerName: "Rank", width: 72, sortable: false,
      renderCell: ({ value }) => <RankBadge rank={value} />,
    },
    {
      field: "studentName", headerName: "Student Name", flex: 1, minWidth: 130,
      renderCell: ({ value, row }) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={row.rank <= 3 ? 700 : 400}
            sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {value}
          </Typography>
          {row.isTopTenPercent && (
            <Chip
              label="Top 10%"
              size="small"
              sx={{
                height: 20,
                bgcolor: `${SUCCESS}22`,
                color: SUCCESS,
                fontWeight: 700,
                fontSize: "0.68rem",
              }}
            />
          )}
        </Box>
      ),
    },
    {
      field: "rollNumber", headerName: "Roll No.", width: 100,
      renderCell: ({ value }) => (
        <Typography variant="body2" color="text.secondary" fontSize="0.78rem">
          {value || "—"}
        </Typography>
      ),
    },
    {
      field: "problemsSolved", headerName: "Solved", width: 88, type: "number",
      renderCell: ({ value }) => (
        <Tooltip title="Unique accepted problems solved in selected recent window" arrow>
          <Typography variant="body2" fontWeight={700} sx={{ color: PRIMARY }}>
            {value ?? 0}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "activeWeeks", headerName: "Active Weeks", width: 110, type: "number",
      renderCell: ({ value }) => (
        <Tooltip title="Number of ISO weeks with accepted activity in selected window" arrow>
          <Typography variant="body2" fontWeight={600}>
            {value ?? 0}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "syncedSolvedProblems", headerName: "Tracked Solved", width: 110, type: "number",
      renderCell: ({ value }) => (
        <Tooltip title="Problems detected via submission sync" arrow>
          <Typography variant="body2" fontWeight={700}>{value ?? 0}</Typography>
        </Tooltip>
      ),
    },
    {
      field: "easySolved", headerName: "Easy", width: 66, type: "number",
      renderCell: ({ value }) => (
        <Typography variant="body2" fontWeight={600} sx={{ color: SUCCESS }}>{value ?? 0}</Typography>
      ),
    },
    {
      field: "mediumSolved", headerName: "Medium", width: 78, type: "number",
      renderCell: ({ value }) => (
        <Typography variant="body2" fontWeight={600} sx={{ color: WARNING }}>{value ?? 0}</Typography>
      ),
    },
    {
      field: "hardSolved", headerName: "Hard", width: 66, type: "number",
      renderCell: ({ value }) => (
        <Typography variant="body2" fontWeight={600} sx={{ color: DANGER }}>{value ?? 0}</Typography>
      ),
    },
    {
      field: "contestRating", headerName: "Contest Rating", width: 116, type: "number",
      renderCell: ({ value }) =>
        value != null
          ? <Chip label={Math.round(value)} size="small"
              sx={{ bgcolor: "action.selected", fontWeight: 700, fontSize: "0.72rem" }} />
          : <Typography variant="body2" color="text.disabled">—</Typography>,
    },
  ];

  const hardCols = [
    {
      field: "rank", headerName: "Rank", width: 66, sortable: false,
      renderCell: ({ value }) => <RankBadge rank={value} />,
    },
    {
      field: "studentName", headerName: "Student", flex: 1, minWidth: 120,
      renderCell: ({ value, row }) => (
        <Typography variant="body2" fontWeight={row.rank <= 3 ? 700 : 400}
          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {value}
        </Typography>
      ),
    },
    {
      field: "hardSolved", headerName: "Hard", width: 70, type: "number",
      renderCell: ({ value }) => (
        <Typography variant="body2" fontWeight={700} sx={{ color: DANGER }}>{value ?? 0}</Typography>
      ),
    },
    {
      field: "mediumSolved", headerName: "Medium", width: 78, type: "number",
      renderCell: ({ value }) => (
        <Typography variant="body2" fontWeight={600} sx={{ color: WARNING }}>{value ?? 0}</Typography>
      ),
    },
    {
      field: "easySolved", headerName: "Easy", width: 66, type: "number",
      renderCell: ({ value }) => (
        <Typography variant="body2" fontWeight={600} sx={{ color: SUCCESS }}>{value ?? 0}</Typography>
      ),
    },
    {
      field: "syncedSolvedProblems", headerName: "Tracked Solved", width: 110, type: "number",
      renderCell: ({ value }) => (
        <Tooltip title="Problems detected via submission sync" arrow>
          <Typography variant="body2" fontWeight={600}>{value ?? 0}</Typography>
        </Tooltip>
      ),
    },
  ];

  const weeklyCols = [
    {
      field: "rank", headerName: "Rank", width: 66, sortable: false,
      renderCell: ({ value }) => <RankBadge rank={value} />,
    },
    {
      field: "studentName", headerName: "Student", flex: 1, minWidth: 120,
      renderCell: ({ value, row }) => (
        <Typography variant="body2" fontWeight={row.rank <= 3 ? 700 : 400}
          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {value}
        </Typography>
      ),
    },
    {
      field: "weeklySolvedCount", headerName: "Solved (7d)", width: 110, type: "number",
      renderCell: ({ value }) => (
        <Chip
          label={value ?? 0}
          size="small"
          sx={{
            bgcolor: (value ?? 0) > 0 ? `${PRIMARY}22` : "action.selected",
            color:   (value ?? 0) > 0 ? PRIMARY : "text.secondary",
            fontWeight: 700,
          }}
        />
      ),
    },
    {
      field: "syncedSolvedProblems", headerName: "Tracked Solved", width: 110, type: "number",
      renderCell: ({ value }) => (
        <Tooltip title="Problems detected via submission sync" arrow>
          <Typography variant="body2" color="text.secondary">{value ?? 0}</Typography>
        </Tooltip>
      ),
    },
  ];

  const improvedCols = [
    {
      field: "rank", headerName: "Rank", width: 66, sortable: false,
      renderCell: ({ value }) => <RankBadge rank={value} />,
    },
    {
      field: "studentName", headerName: "Student", flex: 1, minWidth: 120,
      renderCell: ({ value, row }) => (
        <Typography variant="body2" fontWeight={row.rank <= 3 ? 700 : 400}
          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {value}
        </Typography>
      ),
    },
    {
      field: "improvement", headerName: "Solved (30d)", width: 112, type: "number",
      renderCell: ({ value }) => (
        <Chip
          label={`+${value ?? 0}`}
          size="small"
          sx={{ bgcolor: `${SUCCESS}22`, color: SUCCESS, fontWeight: 700 }}
        />
      ),
    },
    {
      field: "syncedSolvedProblems", headerName: "Tracked Solved", width: 110, type: "number",
      renderCell: ({ value }) => (
        <Tooltip title="Problems detected via submission sync" arrow>
          <Typography variant="body2" fontWeight={600}>{value ?? 0}</Typography>
        </Tooltip>
      ),
    },
    {
      field: "prevTotal", headerName: "Est. Prev.", width: 88, type: "number",
      renderCell: ({ value }) => (
        <Typography variant="body2" color="text.secondary">{value ?? 0}</Typography>
      ),
    },
  ];

  const inactiveCols = [
    {
      field: "studentName", headerName: "Student Name", flex: 1, minWidth: 130,
      renderCell: ({ value }) => (
        <Typography variant="body2" fontWeight={600}
          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {value}
        </Typography>
      ),
    },
    {
      field: "rollNumber", headerName: "Roll No.", width: 100,
      renderCell: ({ value }) => (
        <Typography variant="body2" color="text.secondary" fontSize="0.78rem">
          {value || "—"}
        </Typography>
      ),
    },
    {
      field: "syncedSolvedProblems", headerName: "Tracked Solved", width: 110, type: "number",
      renderCell: ({ value }) => (
        <Tooltip title="Problems detected via submission sync" arrow>
          <Typography variant="body2" fontWeight={600}>{value ?? 0}</Typography>
        </Tooltip>
      ),
    },
    {
      field: "lastSubmissionDate", headerName: "Last Submission", flex: 1, minWidth: 140, sortable: false,
      renderCell: ({ value }) => (
        <Chip
          label={fmtDate(value)}
          size="small"
          sx={{ bgcolor: `${WARNING}22`, color: WARNING, fontWeight: 600, fontSize: "0.7rem" }}
        />
      ),
    },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box mt={3}>
      {/* Section heading */}
      <Box display="flex" alignItems="center" gap={1.5} mb={2}>
        <EmojiEventsRoundedIcon sx={{ color: GOLD, fontSize: 26 }} />
        <Typography variant="h6" fontWeight={800} sx={{ ...gradientTextSx }}>
          LeetCode Leaderboard
        </Typography>
        {leaderboard.batchName && (
          <Chip
            label={leaderboard.batchName}
            size="small"
            sx={{ bgcolor: "action.selected", fontWeight: 600, fontSize: "0.7rem" }}
          />
        )}
      </Box>

      <Box spacing={2} display="flex" flexDirection="column" sx={{ gap: 2 }}>

        {/* ── 1. Overall Coding Rank ── */}
        {/* <Grid item xs={12}> */}
          <SectionCard icon={EmojiEventsRoundedIcon} iconColor={GOLD} title="Overall Coding Rank — Top 20">
            <CommonDataGrid
              {...BASE_GRID_PROPS}
              rows={overallLeaderboard.map((s) => ({ ...s, id: s.rank }))}
              columns={overallCols}
              getRowClassName={({ row }) => row.rank <= 3 ? "row-gold" : ""}
              sx={baseGridSx}
            />
          </SectionCard>
        {/* </Grid> */}

        {/* ── 2. Hard Problem Crushers ── */}
        <Grid item xs={12}>
          <SectionCard icon={LocalFireDepartmentRoundedIcon} iconColor={DANGER} title="Hard Problem Crushers — Top 10">
            <CommonDataGrid
              {...BASE_GRID_PROPS}
              pagination={false}
              rows={hardProblemCrushers.map((s) => ({ ...s, id: s.rank }))}
              columns={hardCols}
              sx={baseGridSx}
            />
          </SectionCard>
        </Grid>

        {/* ── 3. Weekly Coding Rank ── */}
        <Grid item xs={12}>
          <SectionCard icon={CalendarTodayRoundedIcon} iconColor={PRIMARY} title="Weekly Coding Rank — Last 7 Days">
            <CommonDataGrid
              {...BASE_GRID_PROPS}
              pagination={false}
              rows={weeklyCodingRank.map((s) => ({ ...s, id: s.rank }))}
              columns={weeklyCols}
              sx={baseGridSx}
            />
          </SectionCard>
        </Grid>

        {/* ── 4. Most Improved + Inactive Students side by side ── */}
        <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
                <SectionCard icon={TrendingUpRoundedIcon} iconColor={SUCCESS} title="Most Improved — Last 30 Days">
                    <CommonDataGrid
                        {...BASE_GRID_PROPS}
                        pagination={false}
                        rows={improvedRows}
                        columns={improvedCols}
                        sx={baseGridSx}
                    />
                </SectionCard>
            </Grid>
            <Grid item xs={12} md={6}>
                <SectionCard icon={WarningAmberRoundedIcon} iconColor={WARNING} title="Inactive Students — No Activity in 30 Days">
                    <CommonDataGrid
                        {...BASE_GRID_PROPS}
                        rows={inactiveRows}
                        columns={inactiveCols}
                        getRowClassName={() => "row-inactive"}
                        slots={{ noRowsOverlay: AllActiveOverlay }}
                        sx={{ ...baseGridSx, "--DataGrid-overlayHeight": "80px" }}
                    />
                </SectionCard>
            </Grid>
        </Grid>

      </Box>
    </Box>
  );
};

export default LeetCodeLeaderboard;
